import { RowData } from '@tanstack/react-table';
import classnames from 'classnames';

import { IconButtonSize, IconButtonVariant } from '@xemplo/icon-button';
import { DawnCross } from '@xemplo/icons';

import { useTableSelection } from '../hooks/use-table-selection';
import { DefaultItemCounter } from '../item-counter';
import { TableProps } from '../table.types';

import * as S from './action-bar.style';
import { ActionBarProps } from './action-bar.types';
import { SelectAllRows } from './select-all';
import { SelectedRowCounter } from './selected-row-counter';

export const ActionBarTestId = {
  container: 'action-bar-container',
  counter: 'action-bar-counter',
  selectedCounter: 'action-bar-selected-counter',
  customActions: 'action-bar-custom-actions',
  divider: 'action-bar-divider',
  selector: 'action-bar-selector',
  closeButton: 'action-bar-close-button',
};

export function ActionBar<TData extends RowData>(props: ActionBarProps<TData>) {
  const { table, alwaysShowActionBar } = props;
  const { hasSelected } = useTableSelection(table);

  const { enableRowSelection } = table.options as TableProps<TData>;
  // The action bar is shown when selection is enabled (for bulk actions) or when
  // the consumer opts in via alwaysShowActionBar (for selection-independent actions).
  if (!enableRowSelection && !alwaysShowActionBar) return null;

  return (
    <S.Container
      className={classnames('action-bar-container', { 'has-selected': hasSelected })}
      data-testid={ActionBarTestId.container}
    >
      <DefaultActionBar {...props} />
    </S.Container>
  );
}

function DefaultActionBar<TData>({ table, alwaysShowActionBar }: ActionBarProps<TData>) {
  const { selectedActions, enableRowSelection } = table.options as TableProps<TData>;
  const { hasSelected } = useTableSelection(table);

  if (hasSelected) {
    return (
      <>
        <S.CloseButton
          ariaLabel="close action bar button"
          id="close-action-bar"
          naked
          size={IconButtonSize.Small}
          variant={IconButtonVariant.Lighter}
          onClick={() => table.resetRowSelection()}
        >
          <DawnCross fontSize={16} />
        </S.CloseButton>
        <SelectedRowCounter table={table} />
        <S.Divider className="light" />
        <SelectAllRows table={table} />
        {selectedActions && (
          <S.SelectedActions className={'has-selected'}>
            {selectedActions({ table })}
          </S.SelectedActions>
        )}
      </>
    );
  }

  return (
    <>
      <DefaultItemCounter table={table} testId={ActionBarTestId.counter} />
      {/* The divider and select-all only make sense when selection is enabled;
          SelectAllRows self-gates to null when selection is a boolean false. */}
      {!!enableRowSelection && <S.Divider />}
      <SelectAllRows table={table} />
      {selectedActions && alwaysShowActionBar && (
        <S.SelectedActions>{selectedActions({ table })}</S.SelectedActions>
      )}
    </>
  );
}
